// `window` is provided by ESLint's globals.browser preset (eslint.config.js).
/*
 * src/data/pricing.js — single source of truth for tier prices, currencies and
 * the per-locale currency behaviour of the Pricing section.
 *
 * WHY THIS FILE EXISTS
 *
 * These tables used to live inside `src/Pricing.jsx`. They moved out because
 * the JSON-LD generator (`scripts/build-jsonld.js`) needs the same numbers to
 * emit `SoftwareApplication.offers`, and a Node build script cannot read a JSX
 * component. Structured-data prices MUST match what the visitor sees — a
 * mismatch is worse than omitting `offers` entirely — so both consumers now
 * read one table instead of keeping two copies in sync by hand.
 *
 * DO NOT inline tier prices anywhere else.
 *
 * Shape:
 *   CURRENCIES[sign] = {
 *     sign,   // display glyph, also the toggle key
 *     iso,    // ISO 4217 code — required by schema.org `priceCurrency`
 *     rates,  // { starter, pro, scale } monthly price, whole units
 *     ppu,    // pay-per-use rate, display string
 *   }
 *
 * `iso` exists only for structured data; the UI renders `sign`.
 *
 * PLN/RON rates are hand-tuned constants (no FX feed) — confirm with founder
 * before public launch. Changing a number here changes both the rendered card
 * and the emitted JSON-LD, which is the point.
 *
 * Wiring: this script MUST load BEFORE src/Pricing.jsx in src/main.entry.js.
 * Node-side consumers load it the same way `scripts/extract-i18n-strings.js`
 * loads the i18n runtime — in a node:vm sandbox with a `window` stub — so keep
 * this file free of JSX and of any DOM access.
 */

// Currency catalogue — UAH first (default for UA market), USD for legacy/US users,
// then EU currencies (EUR, PLN, RON) added in Phase 3.1 for EU market entry.
const CURRENCIES = {
  "₴": { sign: "₴", iso: "UAH", rates: { starter: 5900, pro: 13900, scale: 35900 }, ppu: "19" },
  "$": { sign: "$", iso: "USD", rates: { starter: 149, pro: 349, scale: 899 }, ppu: "0.50" },
  "€": { sign: "€", iso: "EUR", rates: { starter: 129, pro: 319, scale: 819 }, ppu: "0.45" },
  "zł": { sign: "zł", iso: "PLN", rates: { starter: 599, pro: 1399, scale: 3599 }, ppu: "1.95" },
  "lei": { sign: "lei", iso: "RON", rates: { starter: 649, pro: 1499, scale: 3899 }, ppu: "2.10" },
};

// Locale -> default currency (Phase 3.2). User can still toggle manually.
const DEFAULT_CUR_FOR_LOCALE = {
  uk: "₴",
  ru: "₴",
  en: "€",
  pl: "zł",
  ro: "lei",
};

// Currency-toggle order per locale (Phase 3.5). EU locales lead with their
// home currency; UA/RU keep the legacy ₴/$/€ ordering with PLN/RON appended
// so the toggle still has all five options.
const TOGGLE_ORDER = {
  uk: ["₴", "$", "€", "zł", "lei"],
  ru: ["₴", "$", "€", "zł", "lei"],
  en: ["€", "zł", "lei", "$", "₴"],
  pl: ["zł", "€", "lei", "$", "₴"],
  ro: ["lei", "€", "zł", "$", "₴"],
};

// Tiers sold self-service. `scale` is enterprise sales (not self-service, see
// .ai-factory/DESCRIPTION.md §Платежи) and is therefore excluded from the
// structured-data offers — advertising a machine-readable price for a tier
// nobody can actually buy online would be a false signal.
const SELF_SERVICE_TIERS = ["starter", "pro"];

const FALLBACK_CURRENCY = "₴";

function defaultCurrencyForLocale(locale) {
  return DEFAULT_CUR_FOR_LOCALE[locale] || FALLBACK_CURRENCY;
}

function toggleOrderForLocale(locale) {
  return TOGGLE_ORDER[locale] || TOGGLE_ORDER.uk;
}

/** Currency record for a locale, falling back to UAH. Used by UI and JSON-LD alike. */
function currencyForLocale(locale) {
  return CURRENCIES[defaultCurrencyForLocale(locale)] || CURRENCIES[FALLBACK_CURRENCY];
}

window.HEALBOT_PRICING = {
  CURRENCIES,
  DEFAULT_CUR_FOR_LOCALE,
  TOGGLE_ORDER,
  SELF_SERVICE_TIERS,
  FALLBACK_CURRENCY,
  defaultCurrencyForLocale,
  toggleOrderForLocale,
  currencyForLocale,
};
